/* global Word, Office, console */

/**
 * Flatten every link in the document to plain text, before extraction.
 *
 * Runs as a preprocessing step so the rest of the pipeline only ever sees
 * ordinary text: every character offset downstream — target indices, the
 * formatting snapshot, span ranges — is derived from the already-flattened
 * text, so there is no offset-invalidation hazard to reason about.
 *
 * Two Word constructs need two different operations:
 *
 *  - Hyperlinks are `w:hyperlink` elements. `delete()` removes the link and
 *    leaves the display text.
 *  - A generated TOC is the *result* of a `TOC` field, with `HYPERLINK` and
 *    `PAGEREF` fields nested inside. Deleting those nested links would be undone
 *    by the next field update, so the durable operation is `Field.unlink()` —
 *    the programmatic Ctrl+Shift+F9.
 *
 * Unlinking the TOC also removes the reason content-control inserts failed with
 * `GeneralException` on TOC rows: Word refuses an insert anywhere inside a field
 * result, and a TOC's visible rows are that result. Once unlinked they are
 * ordinary paragraphs.
 *
 * This is destructive and deliberately so. A `click here` link keeps the words
 * and loses the address, and an unlinked TOC is no longer a field, so its page
 * numbers freeze and F9 no longer updates it. Only Word's own undo, in the same
 * session, can put either back.
 */

import { StepProgress } from "./apply-progress";
import { WORD_DELIMITERS, normalizeStyle } from "./character_styles/applicator";
import { HYPERLINK_CHARACTER_STYLES } from "./character_styles/rules-engine";

import type { ApplyProgressReporter } from "./apply-progress";
import type { PerfSession } from "./perf";

export interface LinkFlatteningResult {
  tocFieldsUnlinked: number;
  hyperlinksFlattened: number;
  /** Ranges whose residual hyperlink character style was reset. */
  stylesCleared: number;
  failedBatches: number;
  /** Neither API set is available, so the step did nothing at all. */
  unsupported: boolean;
}

/**
 * Both APIs are desktop-only and the manifest declares no `<Requirements>`
 * block, so each stage is gated separately rather than under one 1.4 check —
 * that way a desktop build below 1.4 still gets hyperlink flattening.
 */
const UNLINK_REQUIREMENT = { set: "WordApiDesktop", version: "1.4" };
const HYPERLINK_REQUIREMENT = { set: "WordApiDesktop", version: "1.3" };

/**
 * Counts here are tens, not thousands, so this only has to bound a failure's
 * blast radius. It is not a performance constant and does not belong with
 * `PASS1_BATCH_OPERATIONS` and the rest.
 */
const OPS_PER_BATCH = 5;

/**
 * Style resets are counted per *word*, so a TOC of a few dozen rows produces
 * hundreds of them — batching those at `OPS_PER_BATCH` would cost ~80 syncs to
 * do trivial work. Measured style writes are close to linear in this codebase,
 * so a large batch here is free.
 */
const STYLE_RESETS_PER_BATCH = 100;

/**
 * Word's character-style-only default. `styleBuiltIn` cannot express this: its
 * nearest member, `Normal`, is documented as resetting *character and paragraph*
 * style, which would discard the paragraph style of every paragraph holding a
 * link — including a TOC row's `Toc1`/`Toc2`. The string form is locale-fragile,
 * but `normalizeStyle` already assumes English style names throughout.
 */
const DEFAULT_CHARACTER_STYLE = "Default Paragraph Font";

const PROGRESS_STEP = "Removing links";

const batchLabel = (index: number, size: number = OPS_PER_BATCH): string =>
  String(Math.floor(index / size) + 1).padStart(2, "0");

function isSetSupported({ set, version }: { set: string; version: string }): boolean {
  try {
    return Office.context.requirements.isSetSupported(set, version);
  } catch {
    return false;
  }
}

/**
 * A range spanning more than one character style reports something other than a
 * style name — `""` or `"Mixed"` depending on build — and may throw outright.
 * Every such case has to read as "leave it alone", so failures resolve to
 * `undefined` rather than propagating.
 */
function readStyle(range: Word.Range): string | undefined {
  try {
    return range.style;
  } catch {
    return undefined;
  }
}

/**
 * True only for Word's own hyperlink styles. Anything else — a style the author
 * applied after linking, an unrecognised name, a mixed range — is left as it is.
 * The style slot holds one value, so overwriting it when it is not the hyperlink
 * style discards a choice the author made.
 */
function isHyperlinkStyled(range: Word.Range): boolean {
  const style = normalizeStyle(readStyle(range));
  return style !== null && HYPERLINK_CHARACTER_STYLES.has(style);
}

/**
 * Re-queue a failed batch one operation at a time so a single bad item costs
 * only itself. Retrying is safe here, unlike the character-style passes: an
 * operation that already landed simply throws again and is skipped.
 */
async function retryIndividually<T>(
  context: Word.RequestContext,
  perf: PerfSession,
  label: string,
  items: T[],
  queue: (item: T) => void
): Promise<number> {
  let succeeded = 0;
  for (let i = 0; i < items.length; i += 1) {
    try {
      queue(items[i]);
      await perf.timedSync(context, `${label}:retry${i + 1}`);
      succeeded += 1;
    } catch (error) {
      console.warn(`[flattenLinks] ${label}: skipped one item:`, error);
    }
  }
  perf.note(`${label}:retryTotal`, { items: items.length, succeeded });
  return succeeded;
}

/**
 * Reset the residual hyperlink character style across `ranges`, per word, and
 * only where that style is the one actually present.
 *
 * Direct formatting survives without any effort here: bold, italic, size and
 * colour live in the run's own properties rather than its character style, so
 * assigning a style touches only the style reference. A hyperlink the author had
 * also bolded comes out of this as bold plain text.
 */
async function clearHyperlinkStyle(
  context: Word.RequestContext,
  perf: PerfSession,
  label: string,
  ranges: Word.Range[],
  result: LinkFlatteningResult
): Promise<void> {
  if (ranges.length === 0) return;

  const wordCollections = ranges.map((range) => range.getTextRanges(WORD_DELIMITERS, true));
  for (const words of wordCollections) {
    words.load("items/style");
  }
  try {
    await perf.timedSync(context, `flattenLinks:${label}LoadStyles`, () => ({
      ranges: ranges.length,
    }));
  } catch (error) {
    result.failedBatches += 1;
    console.warn(`[flattenLinks] ${label}: could not read styles, leaving them alone:`, error);
    return;
  }

  const styled = wordCollections.flatMap((words) => words.items).filter(isHyperlinkStyled);

  perf.note(`flattenLinks:${label}Styled`, {
    styled: styled.length,
    words: wordCollections.reduce((total, words) => total + words.items.length, 0),
  });

  for (let i = 0; i < styled.length; i += STYLE_RESETS_PER_BATCH) {
    const batch = styled.slice(i, i + STYLE_RESETS_PER_BATCH);
    for (const range of batch) {
      range.style = DEFAULT_CHARACTER_STYLE;
    }
    const syncLabel = `flattenLinks:${label}StyleBatch${batchLabel(i, STYLE_RESETS_PER_BATCH)}`;
    try {
      await perf.timedSync(context, syncLabel, { ranges: batch.length });
      result.stylesCleared += batch.length;
    } catch (error) {
      result.failedBatches += 1;
      console.warn(`[flattenLinks] ${syncLabel} failed, retrying individually:`, error);
      result.stylesCleared += await retryIndividually(context, perf, syncLabel, batch, (range) => {
        range.style = DEFAULT_CHARACTER_STYLE;
      });
    }
  }
}

/**
 * Stage A. Must run before hyperlink flattening: unlinking a TOC destroys the
 * `HYPERLINK` fields nested in its result, so the other order would enumerate
 * links that are about to cease existing.
 */
async function unlinkTocFields(
  context: Word.RequestContext,
  perf: PerfSession,
  result: LinkFlatteningResult
): Promise<void> {
  const fields = context.document.body.fields;
  fields.load("items/type");
  await perf.timedSync(context, "flattenLinks:loadFields", () => ({ fields: fields.items.length }));

  // Only TOC fields. Page numbers, SEQ, REF and index entries are unrelated to
  // this, and unlinking them would freeze content nobody asked to freeze.
  const tocFields = fields.items.filter((field) => field.type === Word.FieldType.toc);
  perf.note("flattenLinks:tocFound", { toc: tocFields.length, fields: fields.items.length });
  if (tocFields.length === 0) return;

  // Replacing a field with its result remaps proxies across the document, so
  // the former TOC ranges have to be tracked to survive their own unlink.
  const formerTocRanges = tocFields.map((field) => {
    const range = field.result;
    context.trackedObjects.add(range);
    return range;
  });

  try {
    for (let i = 0; i < tocFields.length; i += OPS_PER_BATCH) {
      const batch = tocFields.slice(i, i + OPS_PER_BATCH);
      for (const field of batch) {
        field.unlink();
      }
      const syncLabel = `flattenLinks:unlinkBatch${batchLabel(i)}`;
      try {
        await perf.timedSync(context, syncLabel, { fields: batch.length });
        result.tocFieldsUnlinked += batch.length;
      } catch (error) {
        result.failedBatches += 1;
        console.warn(`[flattenLinks] ${syncLabel} failed, retrying individually:`, error);
        result.tocFieldsUnlinked += await retryIndividually(
          context,
          perf,
          syncLabel,
          batch,
          (field) => field.unlink()
        );
      }
    }

    // Now that these rows are no longer a field result, an insert on them would
    // succeed rather than throw — so leaving the hyperlink style here would
    // produce wrongly styled TOC rows instead of a loud exception. Clearing it
    // is what keeps the underline rules off them.
    await clearHyperlinkStyle(context, perf, "toc", formerTocRanges, result);
  } finally {
    for (const range of formerTocRanges) {
      context.trackedObjects.remove(range);
    }
  }
}

/**
 * Stage B. Every remaining hyperlink, with no scheme or `subAddress` filter:
 * none of the links in these manuscripts are wanted, internal ones included.
 * To keep cross-references instead, skip links whose `address` is empty and
 * whose `subAddress` is not.
 */
async function flattenHyperlinks(
  context: Word.RequestContext,
  perf: PerfSession,
  result: LinkFlatteningResult,
  onProgress?: ApplyProgressReporter
): Promise<void> {
  // `Range.hyperlink` (singular) returns only the first link in a range, which
  // cannot handle two links in one paragraph, and a per-paragraph `getRange()`
  // would reintroduce the one-proxy-per-paragraph cost the perf work removed.
  const hyperlinks = context.document.body.getRange().hyperlinks;
  hyperlinks.load("items/address,items/subAddress,items/textToDisplay");
  await perf.timedSync(context, "flattenLinks:loadHyperlinks", () => ({
    links: hyperlinks.items.length,
  }));

  const links = hyperlinks.items;
  if (links.length === 0) return;

  const progress = new StepProgress(PROGRESS_STEP, links.length, onProgress);

  // Read each link's style while the range is still the link's own, and track
  // the ranges so they outlive the deletion that follows.
  const ranges = links.map((link) => {
    const range = link.range;
    context.trackedObjects.add(range);
    range.load("style");
    return range;
  });

  try {
    try {
      await perf.timedSync(context, "flattenLinks:loadLinkStyles", { links: links.length });
    } catch (error) {
      // Unread styles make `isHyperlinkStyled` false for everything below, so
      // the links still get flattened and no style is touched. Failing closed
      // here costs only the blue underline, and the underline rules already skip
      // hyperlink-styled spans.
      result.failedBatches += 1;
      console.warn("[flattenLinks] could not read link styles, leaving them alone:", error);
    }
    const clearStyle = ranges.map(isHyperlinkStyled);

    for (let i = 0; i < links.length; i += OPS_PER_BATCH) {
      const end = Math.min(i + OPS_PER_BATCH, links.length);
      let queuedStyleWrites = 0;
      for (let j = i; j < end; j += 1) {
        // Nothing is inserted — no appended address, no content control.
        links[j].delete();
        if (clearStyle[j]) {
          ranges[j].style = DEFAULT_CHARACTER_STYLE;
          queuedStyleWrites += 1;
        }
      }
      const syncLabel = `flattenLinks:flattenBatch${batchLabel(i)}`;
      try {
        await perf.timedSync(context, syncLabel, { links: end - i });
        result.hyperlinksFlattened += end - i;
        result.stylesCleared += queuedStyleWrites;
      } catch (error) {
        result.failedBatches += 1;
        console.warn(`[flattenLinks] ${syncLabel} failed, retrying individually:`, error);
        const indices = Array.from({ length: end - i }, (_, k) => i + k);
        result.hyperlinksFlattened += await retryIndividually(
          context,
          perf,
          syncLabel,
          indices,
          (j) => {
            links[j].delete();
            if (clearStyle[j]) {
              ranges[j].style = DEFAULT_CHARACTER_STYLE;
            }
          }
        );
      }
      progress.set(end);
    }
  } finally {
    progress.done();
    for (const range of ranges) {
      context.trackedObjects.remove(range);
    }
  }
}

/**
 * Remove every link in the document body, leaving its display text in place.
 *
 * Must run *before* track changes is enabled, so the removals land as silent
 * edits: under `trackAll` every deleted link becomes a revision, which buries a
 * reviewer's own changes and lets a reject restore a link the run has already
 * styled around. Also before the document is locked, since `cannotEdit` blocks
 * programmatic edits.
 *
 * Caveat: this only holds while the document arrives with tracking off. Nothing
 * turns `trackAll` back off after a run, so a second run on the same document
 * finds it already on and the removals are tracked after all.
 *
 * Never throws. Failing to flatten a link leaves the document exactly as this
 * pipeline already handled it before this step existed, and the underline rules
 * still skip hyperlink-styled spans, so a partial result degrades rather than
 * breaking the run.
 */
export async function flattenLinksToPlainText(
  perf: PerfSession,
  onProgress?: ApplyProgressReporter
): Promise<LinkFlatteningResult> {
  const result: LinkFlatteningResult = {
    tocFieldsUnlinked: 0,
    hyperlinksFlattened: 0,
    stylesCleared: 0,
    failedBatches: 0,
    unsupported: false,
  };

  const canUnlink = isSetSupported(UNLINK_REQUIREMENT);
  const canEnumerateHyperlinks = isSetSupported(HYPERLINK_REQUIREMENT);

  // Word on the web supports neither. The editorial workflow is desktop, so the
  // step no-ops there rather than growing an OOXML fallback.
  if (!canUnlink && !canEnumerateHyperlinks) {
    result.unsupported = true;
    perf.note("flattenLinks:unsupported");
    return result;
  }

  try {
    await Word.run(async (context) => {
      if (canUnlink) {
        await unlinkTocFields(context, perf, result);
      }
      if (canEnumerateHyperlinks) {
        await flattenHyperlinks(context, perf, result, onProgress);
      }
    });
  } catch (error) {
    result.failedBatches += 1;
    console.warn("[flattenLinks] step failed, continuing with links intact:", error);
  }

  perf.note("flattenLinks:done", {
    toc: result.tocFieldsUnlinked,
    links: result.hyperlinksFlattened,
    stylesCleared: result.stylesCleared,
    failedBatches: result.failedBatches,
  });
  return result;
}
